import { graphql } from 'gatsby';

import { Grid2 as Grid, styled } from '@mui/material';

import { MewaCircle, MewaCircleData, ResponsiveGrid } from '@mewa/ui';

import { ComponentProps, PossibleContentSections } from '../fragment-and-types';
import { ContentLivePreviewConfig } from '../live-preview/types';

export const SharedMewaCircleFragment = graphql`
  fragment StepParts on STRAPI__COMPONENT_SHARED_MEWA_CIRCLE_STEP {
    description
    headline
    icon
  }

  fragment MewaCircleFragment on STRAPI_MEWA_CIRCLE {
    circleVariant
    locale
    firstStep {
      ...StepParts
    }
    secondStep {
      ...StepParts
    }
    thirdStep {
      ...StepParts
    }
    fourthStep {
      ...StepParts
    }
    fifthStep {
      ...StepParts
    }
  }

  fragment SharedMewaCircleFragment on STRAPI__COMPONENT_SHARED_MEWA_CIRCLE {
    headline: Headline
    circle: Circle {
      ...MewaCircleFragment
    }
    strapi_component
  }
`;

export const componentName = 'shared.mewa-circle';

export type SharedMewaCircleData = {
  headline: string;
  description: string;
  circle: MewaCircleData | null;
  strapi_component: typeof componentName;
};

export type MewaCirclePreview = {
  strapi_component: typeof componentName;
  Headline: string;
  Description: string;
  Circle: MewaCircleData;
};

export const isSharedMewaCircle = (
  component: PossibleContentSections,
): component is SharedMewaCircleData =>
  component.strapi_component === componentName;

export const StrapiSharedMewaCircle = ({
  circle,
  headline,
}: ComponentProps<SharedMewaCircleData>) =>
  circle ? <MewaCircleWithoutMargins circle={circle} title={headline} /> : null;

const MewaCircleWithoutMargins = styled(MewaCircle)(({ theme }) => ({
  [theme.breakpoints.up(0)]: {
    marginTop: 0,
    marginBottom: 0,
  },
}));

const CampaignMewaCircle = styled(MewaCircle)(({ theme }) => ({
  marginTop: '42px',
  marginBottom: '40px',

  [theme.breakpoints.up('mdSm')]: {
    marginTop: '94px',
    marginBottom: '80px',
  },
}));

export const config = {
  isComponent: isSharedMewaCircle,
  Component: StrapiSharedMewaCircle,
  componentName,
};

export const campaignConfig = {
  isComponent: isSharedMewaCircle,
  Component: ({ circle, headline }: ComponentProps<SharedMewaCircleData>) =>
    circle ? (
      <ResponsiveGrid>
        <Grid size={12}>
          <CampaignMewaCircle circle={circle} title={headline} />
        </Grid>
      </ResponsiveGrid>
    ) : null,
  componentName,
};

const populationParams = {
  Circle: {
    populate: {
      firstStep: '*',
      secondStep: '*',
      thirdStep: '*',
      fourthStep: '*',
      fifthStep: '*',
    },
  },
};

export const previewConfig: ContentLivePreviewConfig = {
  populationParams,
  componentName,
  mapProperties: (mewaCircle): SharedMewaCircleData | null =>
    mewaCircle.strapi_component === componentName
      ? {
          ...mewaCircle,
          headline: mewaCircle.Headline,
          description: mewaCircle.Description,
          circle: mewaCircle.Circle,
        }
      : null,
};
